// src/components/BillingDashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { TeamPlanContext } from '../App';
import { db } from '../firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  deleteDoc,
  doc,
  getDoc,
  writeBatch,
  deleteField
} from 'firebase/firestore';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook
import { pdf } from '@react-pdf/renderer';
import BillingPdf from './BillingPdf';

// Import MUI
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Funzione helper per formattare la valuta (invariata)
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
  }).format(value || 0);
};

function BillingDashboard({ teamId, userRole }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('billing');
  const { isTrial } = useContext(TeamPlanContext);
  
  const [summaries, setSummaries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  
  const isManager = userRole === 'admin' || userRole === 'Engineer';

  // useEffect (invariato)
  useEffect(() => {
    if (!teamId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const q = query(
      collection(db, "billingSummaries"),
      where("teamId", "==", teamId),
      orderBy("createdAt", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // ... (logica invariata)
      const summariesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
      }));
      setSummaries(summariesList);
      setIsLoading(false);
    }, (err) => {
      console.error("Error fetching billing summaries:", err);
      setError("Failed to load billing data."); // <-- Testo errore (tradotto sotto)
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [teamId]);

  // --- Funzioni (MODIFICATE con t()) ---

  const handleDelete = async (id) => {
    if (!isManager) {
      alert(t('delete.denied')); // Tradotto
      return;
    }
    if (window.confirm(t('delete.confirm'))) { // Tradotto
      try {
        const summaryRef = doc(db, "billingSummaries", id);
        const summarySnap = await getDoc(summaryRef);
        
        if (summarySnap.exists()) {
          const data = summarySnap.data();
          const batch = writeBatch(db);
          
          if (data.importedWorkOrderIds && Array.isArray(data.importedWorkOrderIds)) {
            data.importedWorkOrderIds.forEach(woId => {
              const woRef = doc(db, "workOrders", woId);
              batch.update(woRef, { billingSummaryId: deleteField() });
            });
          }
          
          batch.delete(summaryRef);
          await batch.commit();
        }
      } catch (error) {
        console.error("Error deleting summary:", error);
        alert(t('delete.error')); // Tradotto
      }
    }
  };

  const handleEdit = (id) => {
    navigate(`/billing/${id}/edit`);
  };

  // --- MODIFICA QUI: handleExportXLSX ---
  const handleExportXLSX = () => {
    if (summaries.length === 0) {
      alert(t('dashboard.export_error')); // Tradotto
      return;
    }
    // Traduci le intestazioni dell'export
    const dataToExport = summaries.map(summary => ({
      [t('columns.title')]: summary.titolo,
      [t('columns.driver')]: summary.pilotaName,
      [t('columns.status')]: summary.status,
      [t('columns.amount')]: summary.totals?.grandTotal || 0,
      [t('export.labor_cost')]: summary.totals?.labor || 0, // <-- MODIFICATO
      [t('export.parts_cost')]: summary.totals?.parts || 0, // <-- MODIFICATO
      [t('export.manual_cost')]: summary.totals?.manual || 0, // <-- MODIFICATO
      [t('columns.date')]: summary.createdAt ? format(summary.createdAt, 'dd/MM/yyyy') : 'N/A',
      [t('export.id')]: summary.id // <-- MODIFICATO
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    ws['!cols'] = [
      { wch: 30 }, { wch: 20 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Billing Summaries");
    XLSX.writeFile(wb, `Billing_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };
  // --- FINE MODIFICA ---

  // Funzione Quick Export (MODIFICATA con t())
  const handleQuickExport = async (summary) => {
    if (!summary || exportingId) return;
    setExportingId(summary.id);
    try {
      const teamRef = doc(db, "teams", teamId);
      const driverRef = doc(db, "drivers", summary.pilotaId);
      const [teamSnap, driverSnap] = await Promise.all([ getDoc(teamRef), getDoc(driverRef) ]);
      const teamData = teamSnap.data() || {};
      const driverData = driverSnap.data() || {};

      // Proxy istantaneo edge per aggirare CORS senza gravare sul tempo di generazione
      let proxyLogoUrl = null;
      if (teamData.logoUrlLight) {
          proxyLogoUrl = `https://wsrv.nl/?url=${encodeURIComponent(teamData.logoUrlLight)}&output=png`;
      }

      const blob = await pdf(<BillingPdf summary={summary} teamData={teamData} driverData={driverData} logoBase64={proxyLogoUrl} t={t} currentLang={i18n.language} showWatermark={isTrial} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Fattura_${summary.titolo.replace(/\s/g, '_')}_${summary.id.substring(0, 5)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Error exporting data:", err);
      alert("Error during export: " + err.message);
    } finally {
      setExportingId(null);
    }
  };

  // --- Colonne (MODIFICATE con t()) ---
  const columns = [
    { 
      field: 'titolo', 
      headerName: t('columns.title'), // Tradotto
      flex: 2,
      renderCell: (params) => (
        <Button 
          component={RouterLink} 
          to={`/billing/${params.row.id}`}
          sx={{ textTransform: 'none', fontWeight: 600, padding: 0, justifyContent: 'flex-start' }}
        >
          {params.value}
        </Button>
      )
    },
    { field: 'pilotaName', headerName: t('columns.driver'), flex: 1 }, // Tradotto
    { 
      field: 'status', 
      headerName: t('columns.status'), // Tradotto
      flex: 1,
      renderCell: (params) => {
        const status = params.value;
        let color = 'default';
        if (status === 'draft') color = 'warning';
        if (status === 'finalized') color = 'primary';
        if (status === 'paid') color = 'success';
        return <Chip label={status} color={color} size="small" />;
      }
    },
    { 
      field: 'grandTotal', 
      headerName: t('columns.amount'), // Tradotto
      flex: 1,
      type: 'number',
      valueGetter: (value, row) => row.totals?.grandTotal || 0,
      renderCell: (params) => formatCurrency(params.value),
      align: 'right',
      headerAlign: 'right'
    },
    { 
      field: 'createdAt', 
      headerName: t('columns.date'), // Tradotto
      flex: 1,
      type: 'dateTime',
      valueFormatter: (value) => {
        return value ? format(value, 'dd/MM/yyyy HH:mm') : 'N/A';
      }
    },
    {
      field: 'actions',
      headerName: t('columns.actions'), // Tradotto
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      width: 150,
      renderCell: (params) => {
        if (!isManager) return null;
        const isFinalized = params.row.status === 'finalized';
        const isDraft = params.row.status === 'draft';

        return (
          <Box>
            {isFinalized && (
              <Tooltip title={t('tooltips.export_receipt')}> {/* Tradotto */}
                <IconButton 
                  size="small" 
                  color="success"
                  onClick={() => handleQuickExport(params.row)}
                  disabled={exportingId === params.row.id}
                >
                  {exportingId === params.row.id ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
                </IconButton>
              </Tooltip>
            )}

            {(isDraft || isFinalized) && (
              <Tooltip title={t('tooltips.edit')}> {/* Tradotto */}
                <IconButton 
                  size="small" 
                  onClick={() => handleEdit(params.row.id)}
                  disabled={exportingId === params.row.id}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t('tooltips.delete')}> {/* Tradotto */}
              <IconButton 
                size="small" 
                color="error" 
                onClick={() => handleDelete(params.row.id)}
                disabled={exportingId === params.row.id}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Messaggio di errore (MODIFICATO con t()) ---
  if (error) {
    return <Alert severity="error">{t('dashboard.load_error')}</Alert>;
  }

  return (
    <>
      {/* 1. Header (MODIFICATO con t()) */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {t('dashboard.title')} {/* Tradotto */}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.subtitle')} {/* Tradotto */}
          </Typography>
        </Box>
        {isManager && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/billing/new')}
              sx={{ height: '56px' }}
            >
              {t('dashboard.new_summary')} {/* Tradotto */}
            </Button>
          </Box>
        )}
      </Paper>

      {/* 2. Tabella (invariata, usa 'columns' già tradotte) */}
      <Paper 
        elevation={0} 
        sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper',
          height: '70vh',
          width: '100%'
        }}
      >
        <DataGrid
          rows={summaries}
          columns={columns}
          loading={isLoading}
          slots={{ toolbar: GridToolbar }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          getRowId={(row) => row.id} 
        />
      </Paper>
    </>
  );
}

export default BillingDashboard;